import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@c2fo/liquidity';
import { downloadPlayableInvoicesCsv } from '@/enterprise/data/downloadPlayableInvoicesCsv';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import InvoiceDownloadMenu, {
  InvoiceDownloadMenuScope,
} from '@/enterprise/features/invoiceTables/shared/InvoiceDownloadMenu';
import { buildInvoiceCentralCsvFilename } from '@/utils/buildInvoiceCsvFilename';
import { mapUrlStateToApiSearchFilters, mapUrlStateToIncludedTakerMarkets } from './EligibleInvoicesTable';
import { EligibleInvoiceSource, EligibleInvoiceUrlState } from './types';

/**
 * Filter-bar download for eligible (playable) invoices.
 * "All" uses the full source taker-markets; "filtered" uses list urlState (search + market filters).
 * Menu disable uses unfiltered stats so Download all stays available when filters empty the table.
 */
const EligibleToolbarActions = ({
  source,
  urlState,
  onDownloadSubmitted,
}: {
  source: EligibleInvoiceSource;
  urlState: EligibleInvoiceUrlState;
  onDownloadSubmitted?: (payload: { scope: InvoiceDownloadMenuScope }) => void;
}) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { makerOrganizationUuid, makerOrganizationName, currency, takerMarkets } = source;

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);
  const includedTakerMarkets = useMemo(
    () => mapUrlStateToIncludedTakerMarkets({ urlState, takerMarkets }),
    [urlState, takerMarkets]
  );

  const { data: unfilteredStats } = usePlayableInvoiceStats({
    makerOrganizationUuid,
    takerMarkets,
  });

  const hasRows = (unfilteredStats?.totalCount ?? 0) > 0;

  const sortOptions = {
    sortBy: urlState.sort.key,
    sortDirection: (urlState.sort.direction === 'asc' ? 'ASC' : 'DESC') as 'ASC' | 'DESC',
  };

  const handleDownload = async (scope: InvoiceDownloadMenuScope) => {
    if (!hasRows) {
      return;
    }

    onDownloadSubmitted?.({ scope });

    const isFiltered = scope === 'filtered';

    try {
      await downloadPlayableInvoicesCsv({
        makerOrganizationUuid,
        takerMarkets: isFiltered ? includedTakerMarkets : takerMarkets,
        ...(isFiltered && search ? { search } : {}),
        options: sortOptions,
        filename: buildInvoiceCentralCsvFilename({
          makerName: makerOrganizationName,
          currency,
          invoiceType: 'eligible',
        }),
      });
    } catch {
      showSnackbar({ message: t('core.downloadError') });
    }
  };

  return <InvoiceDownloadMenu disabled={!hasRows} onDownload={handleDownload} />;
};

export default EligibleToolbarActions;
